import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import router from '../router';
import { useStates } from '../router/states';
import { i18n } from '../i18n';
import { getEnvironment } from '../common/functions/common';
import { ApiError, ApiErrorResponse, ApiResponse, HttpClientError } from '../common/models/api.interface';
import { useQuery, useMutation } from '@tanstack/vue-query';
import { queryClient } from './queryClient';

const { onLogout, onShowSpinner, onHideSpinner } = useStates();

// Interfaz para configuración extendida con opciones de caché
export interface CacheOptions {
  enabled: boolean;
  duration?: number; // duración en milisegundos (staleTime en TanStack Query)
  cacheTime?: number; // tiempo que permanece en caché después de inactivo
  key?: string; // clave personalizada opcional
  refetchOnWindowFocus?: boolean; // recargar al enfocar la ventana
  retry?: number | boolean; // número de reintentos o booleano
}

export interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  cache?: CacheOptions;
}

// Sistema de caché simple
class CacheManager {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private pendingRequests: Map<string, Promise<any>> = new Map();
  private debugMode: boolean = false;

  // Generar clave de caché basada en URL y parámetros
  generateCacheKey(url: string, config?: ExtendedAxiosRequestConfig): string {
    if (config?.cache?.key) {
      return config.cache.key;
    }

    const baseKey = url;
    const params = config?.params ? JSON.stringify(config.params) : '';
    return `${baseKey}${params ? `_${params}` : ''}`;
  }

  get(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) {
      return null;
    }

    const now = Date.now();
    const expirationTime = entry.timestamp;

    if (now > expirationTime) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  set(key: string, data: any, duration: number = 60000): void {
    const timestamp = Date.now() + duration;
    this.cache.set(key, { data, timestamp });
  }

  remove(key: string): void {
    this.cache.delete(key);
  }

  invalidate(prefix: string): void {
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => {
      this.cache.delete(key);
    });
  }

  clear(): void {
    this.cache.clear();
  }

  setPendingRequest(key: string, promise: Promise<any>): void {
    this.pendingRequests.set(key, promise);
  }

  getPendingRequest(key: string): Promise<any> | null {
    const request = this.pendingRequests.get(key);
    return request || null;
  }

  removePendingRequest(key: string): void {
    this.pendingRequests.delete(key);
  }

  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }
}

// Crear instancia del gestor de caché
export const cacheManager = new CacheManager();

const config = getEnvironment();
const axiosClient: AxiosInstance = axios.create({
  baseURL: config,
});

function getUnauthorized(): string {
  const unauthorized = i18n.global.t('shared.unauthorized');
  return unauthorized;
}

function getUnhandledError(): string {
  const unhandledError = i18n.global.t('shared.unhandledError');
  return unhandledError;
}

function getMustLoginAgain(): string {
  const mustLoginAgain = i18n.global.t('shared.mustLoginAgain');
  return mustLoginAgain;
}

// Importar PrimeVue toast service
import { useToast } from 'primevue/usetoast';

// Variable para almacenar la instancia del toast
let toastInstance: any = null;

// Función para mostrar toast desde cualquier parte
export function showToast(severity: string, summary: string, detail: string, life: number = 3000) {
  // Si estamos en un componente Vue, usar el toast directamente
  if (toastInstance) {
    toastInstance.add({
      severity,
      summary,
      detail,
      life,
    });
  }
}

// Función para establecer la instancia del toast desde App.vue
export function setToastInstance(toast: any) {
  toastInstance = toast;
}

function handleError(error: ApiError): void {
  if (axios.isAxiosError(error)) {
    // Network error (no response): server unreachable or mobile connectivity issue.
    // Do NOT log out — the session is still valid, it's just a connectivity problem.
    if (!error.response) {
      console.warn('Network error – no response from server:', error.message);
      return;
    }

    const { data, status, config } = error.response;

    // Comprobar si es un error en la página de login
    // No redirigir al login si ya estamos intentando hacer login
    if (
      (status === 401 || status === 403) &&
      error.config &&
      error.config.url &&
      error.config.url.includes('api/auth/login')
    ) {
      // No mostrar toast global en login; dejar que el componente Login.vue maneje el error
      // para evitar mensajes duplicados.
      return;
    }

    // Para errores 402: Suscripción vencida o requerida
    if (status === 402) {
      const errorMessage =
        (data as any)?.message || 'Su suscripción ha vencido. Por favor, renueve su plan para continuar.';

      // Mostrar toast con mensaje de suscripción vencida
      showToast('warn', 'Suscripción Requerida', errorMessage, 5000);

      // Redirigir a la página de planes de suscripción si no estamos ya allí
      if (router.currentRoute.value.name !== 'subscription-plans') {
        setTimeout(() => {
          router.push('/subscription-plans');
        }, 1500);
      }

      return;
    }

    // Para otros errores 401 o 403
    if (status === 401 || status === 403) {
      const hasToken = !!sessionStorage.getItem('authToken');

      // Si el usuario TIENE sesión iniciada, mantener el comportamiento de logout/redirección
      if (hasToken) {
        // Mostrar mensaje toast de sesión caducada
        showToast('error', 'Error', getUnauthorized());

        onLogout();
        // Eliminar token de autenticación
        sessionStorage.removeItem('authToken');
        // Mantener otras configuraciones en localStorage
        // No usar localStorage.clear() para preservar configuraciones como lang y layout_config
        setTimeout(() => {
          router.push('/login');
        }, 2500);
      }
      // Si NO hay sesión (sin token), no mostrar mensaje por defecto ni redirigir.
      // Se permite que el error sea gestionado por los métodos get/post/put para propagar
      // el mensaje del backend hacia los componentes.
    }
  } else {
    console.error('Non-Axios error:', error);
  }
}

function handleErrorResponse(data: ApiErrorResponse, status: number): ApiErrorResponse | never {
  if (status === 400 || status === 500 || status === 409 || status === 404) {
    return data;
  } else if (status === 403) {
    // Para errores 403, devolver el mensaje de error directamente
    // Esto incluye el caso de usuario inactivo
    if (typeof data === 'string' && data && (data as string).trim()) {
      throw new Error(data);
    } else if (typeof data === 'object' && data && (data as any).message) {
      throw new Error((data as any).message);
    } else {
      throw new Error(getMustLoginAgain());
    }
  } else if (status === 401) {
    throw new Error(getUnauthorized());
  }
  throw new Error(getUnhandledError());
}

function init(): void {
  axiosClient.interceptors.request.use((config) => {
    const token = sessionStorage.getItem('authToken');
    const lang = localStorage.getItem('lang');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (lang) {
      config.headers['Accept-Language'] = lang;
    }
    return config;
  });

  axiosClient.interceptors.response.use(
    (response) => response,
    (error: ApiError) => {
      // Manejar el error (logout y redirección para 401/403)
      handleError(error);

      // Devolver el error para que sea manejado por los métodos get/post/put
      return Promise.reject(error);
    },
  );
}

async function post<T>(url: string, data: unknown, config?: ExtendedAxiosRequestConfig): ApiResponse<T> {
  onShowSpinner();
  try {
    const defaultConfig = {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    };

    const mergedConfig = config ? { ...defaultConfig, ...config } : defaultConfig;

    if (config && config.responseType === 'blob') {
      const response = await axiosClient.post(url, data, mergedConfig);
      onHideSpinner();
      return response.data;
    } else {
      const { data: response } = await axiosClient.post<T>(url, data, mergedConfig);
      onHideSpinner();
      return response;
    }
  } catch (error) {
    onHideSpinner();
    if (axios.isAxiosError(error)) {
      const { data, status } = error.response as HttpClientError;

      // Para errores 402: mostrar mensaje de suscripción vencida y redirigir
      if (status === 402) {
        const errorMessage = data?.message || 'Su suscripción ha vencido. Por favor, renueve su plan para continuar.';
        showToast('warn', 'Suscripción Requerida', errorMessage, 5000);

        // Redirigir a subscription-plans si no estamos ya allí
        if (router.currentRoute.value.name !== 'subscription-plans') {
          setTimeout(() => {
            router.push('/subscription-plans');
          }, 1500);
        }

        // No propagar el error después de manejar el 402
        return new Promise(() => {});
      }

      // Para errores 401/403: si hay token, el interceptor maneja (logout/redirección) y aquí se evita propagar
      // Si NO hay token, se propaga el error con el mensaje del backend para que el componente lo muestre
      if (status === 401 || status === 403) {
        // Excepción específica para el endpoint de login: SIEMPRE propagar el error al
        // componente Login.vue para que pueda mostrar el mensaje específico del backend
        // (credenciales incorrectas, último intento, usuario bloqueado, usuario inactivo).
        // Esto evita que un token residual (de una sesión previa cerrada en otra pestaña)
        // haga que la promesa se quede colgada y el usuario no vea ningún mensaje.
        const isLoginEndpoint = typeof url === 'string' && url.includes('api/auth/login');
        const hasToken = !!sessionStorage.getItem('authToken');
        if (hasToken && !isLoginEndpoint) {
          return new Promise(() => {});
        }
        throw handleErrorResponse(data, status);
      }

      throw handleErrorResponse(data, status);
    }
    throw new Error(getUnhandledError());
  }
}

async function put<T>(url: string, data: unknown): ApiResponse<T> {
  onShowSpinner();
  try {
    const { data: response } = await axiosClient.put<T>(url, data, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    onHideSpinner();
    return response;
  } catch (error) {
    onHideSpinner();
    if (axios.isAxiosError(error)) {
      const { data, status } = error.response as HttpClientError;

      // Para errores 402: mostrar mensaje de suscripción vencida y redirigir
      if (status === 402) {
        const errorMessage = data?.message || 'Su suscripción ha vencido. Por favor, renueve su plan para continuar.';
        showToast('warn', 'Suscripción Requerida', errorMessage, 5000);

        // Redirigir a subscription-plans si no estamos ya allí
        if (router.currentRoute.value.name !== 'subscription-plans') {
          setTimeout(() => {
            router.push('/subscription-plans');
          }, 1500);
        }

        // No propagar el error después de manejar el 402
        return new Promise(() => {});
      }

      // Para errores 401/403: si hay token, el interceptor maneja (logout/redirección) y aquí se evita propagar
      // Si NO hay token, se propaga el error con el mensaje del backend para que el componente lo muestre
      if (status === 401 || status === 403) {
        const hasToken = !!sessionStorage.getItem('authToken');
        if (hasToken) {
          return new Promise(() => {});
        }
        throw handleErrorResponse(data, status);
      }

      throw handleErrorResponse(data, status);
    }
    throw new Error(getUnhandledError());
  }
}

async function get<T>(url: string, config?: ExtendedAxiosRequestConfig): ApiResponse<T> {
  // Si se solicita caché y está habilitada, usar TanStack Query
  if (config?.cache?.enabled) {
    // Generar clave para la consulta
    const queryKey = config.cache.key || cacheManager.generateCacheKey(url, config);

    // Configurar opciones de la consulta
    const staleTime = config.cache.duration || 60000; // 1 minuto por defecto
    const cacheTime = config.cache.cacheTime || 5 * 60000; // 5 minutos por defecto
    const retry = config.cache.retry !== undefined ? config.cache.retry : 1;
    const refetchOnWindowFocus =
      config.cache.refetchOnWindowFocus !== undefined ? config.cache.refetchOnWindowFocus : false;

    const fetchData = async () => {
      onShowSpinner();
      try {
        if (config && config.responseType === 'blob') {
          const response = await axiosClient.get(url, config);
          onHideSpinner();
          return response.data;
        } else {
          const { data: response } = await axiosClient.get<T>(url, config);
          onHideSpinner();
          return response;
        }
      } catch (error) {
        onHideSpinner();
        if (axios.isAxiosError(error)) {
          const { data, status } = error.response as HttpClientError;

          // Para errores 402: mostrar mensaje de suscripción vencida y redirigir
          if (status === 402) {
            const errorMessage =
              data?.message || 'Su suscripción ha vencido. Por favor, renueve su plan para continuar.';
            showToast('warn', 'Suscripción Requerida', errorMessage, 5000);

            // Redirigir a subscription-plans si no estamos ya allí
            if (router.currentRoute.value.name !== 'subscription-plans') {
              setTimeout(() => {
                router.push('/subscription-plans');
              }, 1500);
            }

            // No propagar el error después de manejar el 402
            return new Promise(() => {});
          }

          // Para errores 401/403: si hay token, el interceptor maneja (logout/redirección) y aquí se evita propagar
          // Si NO hay token, se propaga el error con el mensaje del backend para que el componente lo muestre
          if (status === 401 || status === 403) {
            const hasToken = !!sessionStorage.getItem('authToken');
            if (hasToken) {
              return new Promise(() => {});
            }
            throw handleErrorResponse(data, status);
          }

          throw handleErrorResponse(data, status);
        }
        throw new Error(getUnhandledError());
      }
    };

    return queryClient.fetchQuery({
      queryKey: [queryKey],
      queryFn: fetchData,
      staleTime,
      gcTime: cacheTime,
      retry,
    });
  } else {
    onShowSpinner();
    try {
      if (config && config.responseType === 'blob') {
        const response = await axiosClient.get(url, config);
        onHideSpinner();
        return response.data;
      } else {
        const { data: response } = await axiosClient.get<T>(url, config);
        onHideSpinner();
        return response;
      }
    } catch (error) {
      onHideSpinner();
      if (axios.isAxiosError(error)) {
        const { data, status } = error.response as HttpClientError;

        // Para errores 402: mostrar mensaje de suscripción vencida y redirigir
        if (status === 402) {
          const errorMessage = data?.message || 'Su suscripción ha vencido. Por favor, renueve su plan para continuar.';
          showToast('warn', 'Suscripción Requerida', errorMessage, 5000);

          // Redirigir a subscription-plans si no estamos ya allí
          if (router.currentRoute.value.name !== 'subscription-plans') {
            setTimeout(() => {
              router.push('/subscription-plans');
            }, 1500);
          }

          // No propagar el error después de manejar el 402
          return new Promise(() => {});
        }

        // Para errores 401/403: si hay token, el interceptor maneja (logout/redirección) y aquí se evita propagar
        // Si NO hay token, se propaga el error con el mensaje del backend para que el componente lo muestre
        if (status === 401 || status === 403) {
          const hasToken = !!sessionStorage.getItem('authToken');
          if (hasToken) {
            return new Promise(() => {});
          }
          throw handleErrorResponse(data, status);
        }

        throw handleErrorResponse(data, status);
      }
      throw new Error(getUnhandledError());
    }
  }
}

/**
 * PATCH Request
 * Actualización parcial de recursos en el servidor con manejo de errores
 */
async function patch<T>(url: string, data?: unknown): ApiResponse<T> {
  onShowSpinner();
  try {
    const { data: response } = await axiosClient.patch<T>(url, data, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    onHideSpinner();
    return response;
  } catch (error) {
    onHideSpinner();
    if (axios.isAxiosError(error)) {
      const { data, status } = error.response as HttpClientError;

      // Para errores 402: mostrar mensaje de suscripción vencida y redirigir
      if (status === 402) {
        const errorMessage = data?.message || 'Su suscripción ha vencido. Por favor, renueve su plan para continuar.';
        showToast('warn', 'Suscripción Requerida', errorMessage, 5000);

        // Redirigir a subscription-plans si no estamos ya allí
        if (router.currentRoute.value.name !== 'subscription-plans') {
          setTimeout(() => {
            router.push('/subscription-plans');
          }, 1500);
        }

        // No propagar el error después de manejar el 402
        return new Promise(() => {});
      }

      // Para errores 401/403: si hay token, el interceptor maneja (logout/redirección) y aquí se evita propagar
      // Si NO hay token, se propaga el error con el mensaje del backend para que el componente lo muestre
      if (status === 401 || status === 403) {
        const hasToken = !!sessionStorage.getItem('authToken');
        if (hasToken) {
          return new Promise(() => {});
        }
        throw handleErrorResponse(data, status);
      }

      throw handleErrorResponse(data, status);
    }
    throw new Error(getUnhandledError());
  }
}

/**
 * DELETE Request
 * Eliminación de recursos en el servidor con manejo de errores
 */
async function deleteRequest<T>(url: string): Promise<ApiResponse<T>> {
  onShowSpinner();
  try {
    const { data: response } = await axiosClient.delete<T>(url);
    onHideSpinner();
    return response;
  } catch (error) {
    onHideSpinner();
    if (axios.isAxiosError(error)) {
      const { data, status } = error.response as HttpClientError;

      // Para errores 402: mostrar mensaje de suscripción vencida y redirigir
      if (status === 402) {
        const errorMessage = data?.message || 'Su suscripción ha vencido. Por favor, renueve su plan para continuar.';
        showToast('warn', 'Suscripción Requerida', errorMessage, 5000);

        // Redirigir a subscription-plans si no estamos ya allí
        if (router.currentRoute.value.name !== 'subscription-plans') {
          setTimeout(() => {
            router.push('/subscription-plans');
          }, 1500);
        }

        // No propagar el error después de manejar el 402
        return new Promise(() => {});
      }

      // Para errores 401/403: si hay token, el interceptor maneja (logout/redirección) y aquí se evita propagar
      // Si NO hay token, se propaga el error con el mensaje del backend para que el componente lo muestre
      if (status === 401 || status === 403) {
        const hasToken = !!sessionStorage.getItem('authToken');
        if (hasToken) {
          return new Promise(() => {});
        }
        throw handleErrorResponse(data, status);
      }

      throw handleErrorResponse(data, status);
    }
    throw new Error(getUnhandledError());
  }
}

export default { init, post, put, patch, get, delete: deleteRequest };
